/**
 * Date and Time formatting utilities for DMS
 * All dates: dd-mm-yyyy format
 * All times: 24hr military format (HH:mm)
 */

/**
 * Format a date string (yyyy-mm-dd) to dd-mm-yyyy
 * @param {string} dateStr - Date string in yyyy-mm-dd format
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  // Handle ISO date strings with time
  const datePart = dateStr.split("T")[0];
  const parts = datePart.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

/**
 * Format a Date object to dd-mm-yyyy
 * @param {Date} date - Date object
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
export const formatDateObj = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format time to 24hr military format (HH:mm)
 * Handles various input formats: "14:30", "2:30 PM", "14:30:00"
 * @param {string} timeStr - Time string
 * @returns {string} Formatted time string in HH:mm format
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return "";
  
  // If already in HH:mm format (24hr), return as-is
  if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
    const [hours, mins] = timeStr.split(":");
    return `${hours.padStart(2, "0")}:${mins}`;
  }
  
  // Handle HH:mm:ss format
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeStr)) {
    const [hours, mins] = timeStr.split(":");
    return `${hours.padStart(2, "0")}:${mins}`;
  }
  
  // Handle 12hr format (e.g., "2:30 PM", "2:30PM")
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?$/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const mins = match[2];
    const period = match[3]?.toUpperCase();
    
    if (period === "PM" && hours !== 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }
    
    return `${String(hours).padStart(2, "0")}:${mins}`;
  }
  
  return timeStr;
};

/**
 * Parse dd-mm-yyyy string to Date object
 * @param {string} dateStr - Date string in dd-mm-yyyy format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [day, month, year] = parts;
  const date = new Date(year, month - 1, day);
  return isNaN(date) ? null : date;
};

/**
 * Get current date in yyyy-mm-dd format (for API calls)
 * @returns {string} Current date in yyyy-mm-dd format
 */
export const getTodayISO = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Alias for getTodayISO
export const todayISO = getTodayISO;

/**
 * Get current date in dd-mm-yyyy format (for display)
 * @returns {string} Current date in dd-mm-yyyy format
 */
export const getTodayDisplay = () => {
  return formatDateObj(new Date());
};

// Alias for getTodayDisplay
export const todayDisplay = getTodayDisplay;

/**
 * Convert dd-mm-yyyy back to yyyy-mm-dd (for API calls)
 * @param {string} displayDate - Date in dd-mm-yyyy format
 * @returns {string} Date in yyyy-mm-dd format
 */
export const toISODate = (displayDate) => {
  if (!displayDate) return "";
  const parts = displayDate.split("-");
  if (parts.length !== 3) return displayDate;
  // If first part is 4 digits, already ISO
  if (parts[0].length === 4) return displayDate;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

/**
 * Format an ISO timestamp to IST (Asia/Kolkata) timezone
 * Returns format: dd-mm-yyyy HH:mm
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted timestamp in IST
 */
export const formatISTTimestamp = (timestamp) => {
  if (!timestamp) return "";
  
  try {
    const date = new Date(timestamp);
    // Convert to IST using toLocaleString
    const istString = date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
    const istDate = new Date(istString);
    
    const dd = String(istDate.getDate()).padStart(2, "0");
    const mm = String(istDate.getMonth() + 1).padStart(2, "0");
    const yyyy = istDate.getFullYear();
    const hh = String(istDate.getHours()).padStart(2, "0");
    const min = String(istDate.getMinutes()).padStart(2, "0");
    
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  } catch {
    return timestamp;
  }
};
